import { useState, useEffect } from "react"
import { Loader } from "~/ui-rtk/components/ui/common"
import { CamelSvg } from "~/ui-rtk/components/ui/svg/business"
import type { StepNode } from "../types"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

const LOADING_PHRASES = [
  "Pontificating",
  "Magicking",
  "Crunchercizing",
  "Whammin on the slam jam",
  "Binging the Bangs",
  "Dialing up the modems",
  "Delivering the virtual goods",
  "Zipping and Zapping",
  "Mindmelding",
  "Snapping into a slim jim",
  "Breaking ourselves off a piece of that kit kat bar",
  "Rocking the casbah",
  "Jamming",
  "Hooba-ing the stank",
  "Unearthing data gems",
]

interface StepBarListProps {
  nodes: StepNode[]
  isLoading: boolean
  selectedNodeName: string | undefined
  onNodeClick: (name: string) => void
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
  return `$${value.toFixed(0)}`
}

const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return `${value.toFixed(0)}`
}

export default function StepBarList({
  nodes,
  isLoading,
  selectedNodeName,
  onNodeClick,
}: StepBarListProps) {
  const [phraseIndex, setPhraseIndex] = useState(() =>
    Math.floor(Math.random() * LOADING_PHRASES.length),
  )
  const { isEnabled: isDemoEnabled } = useDemoMode()

  useEffect(() => {
    if (!isLoading) return
    const interval = setInterval(() => {
      setPhraseIndex(prev => {
        let next = Math.floor(Math.random() * LOADING_PHRASES.length)
        while (next === prev) {
          next = Math.floor(Math.random() * LOADING_PHRASES.length)
        }
        return next
      })
    }, 4500)
    return () => clearInterval(interval)
  }, [isLoading])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] gap-4">
        <CamelSvg className="w-12 h-12 opacity-80" />
        <p className="text-sm text-system-gray-300 italic animate-pulse">
          {LOADING_PHRASES[phraseIndex]}...
        </p>
        <Loader size={32} />
      </div>
    )
  }

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-system-gray-400">
        No data available for the selected filters.
      </div>
    )
  }

  // Sort "Unknown" to the bottom
  const sortedNodes = [...nodes].sort((a, b) => {
    if (a.pagePath === "Unknown") return 1
    if (b.pagePath === "Unknown") return -1
    return 0
  })

  const maxSessions = Math.max(...sortedNodes.map(n => n.sessionsCount), 1)

  return (
    <div className="space-y-2">
      {sortedNodes.map(node => {
        const widthPercent = (node.sessionsCount / maxSessions) * 100
        const isSelected = node.pagePath === selectedNodeName
        const pltv = node.revenue + node.predictedLtv
        const pltvPerSession =
          node.sessionsCount > 0 ? pltv / node.sessionsCount : 0

        return (
          <button
            key={node.pagePath}
            className="w-full text-left group"
            onClick={() => onNodeClick(node.pagePath)}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <div
                  className={`h-10 rounded transition-colors ${
                    isSelected
                      ? "bg-blue-500/80 border border-blue-400"
                      : "bg-system-blue-700 border border-system-blue-600 group-hover:bg-system-blue-600"
                  }`}
                  style={{ width: `${Math.max(widthPercent, 8)}%` }}
                />
                <div className="absolute inset-0 flex items-center px-3">
                  <span
                    className={cn(
                      "text-sm text-white font-medium truncate",
                      isDemoEnabled && "blur-md",
                    )}
                  >
                    {node.pagePath}
                  </span>
                </div>
              </div>
              <div className="text-right min-w-[340px] text-sm text-system-gray-300">
                <span className="font-medium text-white">
                  {formatNumber(node.sessionsCount)}
                </span>
                {" sessions | "}
                {formatCurrency(node.revenue)}
                {" rev | "}
                {formatCurrency(pltv)}
                {" pLTV | "}
                {formatCurrency(pltvPerSession)} pLTV/session
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
